import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { auth } from '../services/firebase';
import { authService } from '../services/authService';
import firestoreHealthService from '../services/firestoreHealthService';
import { throttle } from '../utils/throttle';

// Interface pour les données utilisateur sécurisées
interface SecureUserData {
  uid: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  role: 'owner' | 'manager' | 'staff';
  restaurantId: string;
  lastLogin: any;
  isActive: boolean;
  permissions: string[];
}

// Interface pour l'inscription
interface RegisterData {
  email: string;
  password: string;
  restaurantName: string;
  ownerName: string;
  phone: string;
  address: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: SecureUserData | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; requiresVerification?: boolean }>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  refreshSession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<SecureUserData | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Initialiser le service de santé Firestore au démarrage
  useEffect(() => {
    
    // Le service s'initialise automatiquement via son singleton
    firestoreHealthService.checkFirestoreHealth();
  }, []);

  useEffect(() => {
    // Écouter les changements d'authentification Firebase
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {

      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Attendre que le profil soit chargé avec la nouvelle méthode
        authService.waitForUserProfile(10000).then((profile) => {
          // Profil chargé
          setUserProfile(profile);
          setLoading(false);
        });
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Polling pour vérifier si le profil est chargé
  // OPTIMISÉ: Réduit de 500ms à 2000ms et limité à 2 tentatives
  useEffect(() => {
    if (user && !userProfile && !loading) {
      let attempts = 0;
      const maxAttempts = 2;
      
      const checkProfile = () => {
        if (attempts >= maxAttempts) return;
        
        const profile = authService.getUserProfile();
        if (profile) {
          // Profil trouvé via polling
          setUserProfile(profile);
        }
        attempts++;
      };
      
      // Polling réduit: 2s au lieu de 500ms (4x moins d'appels)
      const interval = setInterval(checkProfile, 2000);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        // Timeout: profil non trouvé
      }, 5000);
      
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [user, userProfile, loading]);

  // Rafraîchissement automatique de session sur activité
  // OPTIMISÉ: Throttle à 5 minutes + seulement click/keypress
  useEffect(() => {
    // Throttle: max 1 fois toutes les 5 minutes (au lieu de centaines par seconde!)
    const handleUserActivity = throttle(() => {
      if (user && userProfile) {
        authService.refreshSession();
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Événements réduits: seulement click et keypress (mousemove et scroll enlevés!)
    const events = ['click', 'keypress'];
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      handleUserActivity.cancel(); // Important: annuler le throttle
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [user, userProfile]);

  const login = async (email: string, password: string) => {
    return await authService.login(email, password);
  };

  const register = async (userData: RegisterData) => {
    await authService.register(userData);
  };

  const logout = async () => {
    await authService.logout();
  };

  const resetPassword = async (email: string) => {
    await authService.resetPassword(email);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await authService.changePassword(currentPassword, newPassword);
  };

  const hasPermission = (permission: string): boolean => {
    return authService.hasPermission(permission);
  };

  const refreshSession = (): void => {
    authService.refreshSession();
  };

  const value = {
    user,
    userProfile,
    loading,
    isAuthenticated: !!user && !!userProfile,
    login,
    register,
    logout,
    resetPassword,
    changePassword,
    hasPermission,
    refreshSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};